import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import useCustomizedDataEntrySectionsForTeacher from '../../../../hooks/useCustomizedDataEntrySectionsForTeacher';

const CustomDataEntrySection = ({
  allSelectedSectionIds = [],
  sectionDocId,
  customSectionAnswers = [],
  onCustomSectionChange,
  user,
  dataPoint,
  dataPointIndex = 0,
  // Add new props for time collection configuration
  timeOfCollectionType,
  timeOfCollectionSpecificsList = [],
}) => {
  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  // Find the section's answer object in the array, or create a new one if not present
  const sectionAnswer = customSectionAnswers.find(
    (s) => s.docId === sectionDocId
  ) || { docId: sectionDocId, questions: {} };

  // Use ref to track the last state we sent to parent to prevent circular updates
  const lastSentStateRef = useRef(null);
  const isInternalUpdateRef = useRef(false);
  const previousSectionDocIdRef = useRef(sectionDocId);

  // Initialize state from props only on mount
  const [questionsState, setQuestionsState] = useState(() => {
    return sectionAnswer.questions || {};
  });

  // Only sync state from props when sectionDocId changes (not on every prop update)
  // This prevents the render loop when user types
  useEffect(() => {
    // Reset refs when switching sections
    if (previousSectionDocIdRef.current !== sectionDocId) {
      lastSentStateRef.current = null;
      isInternalUpdateRef.current = false;
      previousSectionDocIdRef.current = sectionDocId;

      // Initialize state for new section
      const currentSectionAnswer = customSectionAnswers.find(
        (s) => s.docId === sectionDocId
      );
      if (currentSectionAnswer?.questions) {
        setQuestionsState(currentSectionAnswer.questions);
      } else {
        setQuestionsState({});
      }
      return;
    }

    // Only sync if update didn't come from our own change
    // This handles external data loading (e.g., saved data loaded after mount)
    if (!isInternalUpdateRef.current) {
      const currentSectionAnswer = customSectionAnswers.find(
        (s) => s.docId === sectionDocId
      );
      if (currentSectionAnswer?.questions) {
        // Only update if the data is actually different (e.g., loaded from server)
        const currentQuestionsStr = JSON.stringify(currentSectionAnswer.questions);
        const lastSentStr = lastSentStateRef.current ? JSON.stringify(lastSentStateRef.current) : null;

        // Only sync if this is different from what we last sent
        // This allows external data to sync in, but prevents circular updates
        if (currentQuestionsStr !== lastSentStr) {
          setQuestionsState(currentSectionAnswer.questions);
          // Update lastSentStateRef to match what we just synced
          lastSentStateRef.current = Array.isArray(currentSectionAnswer.questions)
            ? currentSectionAnswer.questions
            : Object.keys(currentSectionAnswer.questions).map((id) => ({
              id,
              answer: currentSectionAnswer.questions[id],
            }));
        }
      }
    }
    // Reset the flag after processing
    isInternalUpdateRef.current = false;
    // Note: customSectionAnswers is intentionally NOT in deps to prevent render loop
    // The effect runs on sectionDocId change, and reads current customSectionAnswers at that time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionDocId]);

  const [error] = useState('');

  // Fetch all custom data entry sections for the school
  const { data: allCustomSections = [], isLoading: isLoadingSections } =
    useCustomizedDataEntrySectionsForTeacher(user?.schoolId);

  // Find the section definition for this docId
  const sectionDef = allCustomSections.find(
    (section) => section.docId === sectionDocId
  );

  // Check if this section actually exists in the available sections
  const sectionExists = allCustomSections.some(
    (section) => section.docId === sectionDocId
  );

  // When questionsState changes, update the parent with the new customSectionAnswers array
  // useEffect(() => {
  //   // Update only this section's entry in the array
  //   const updatedAnswers = [
  //     ...customSectionAnswers.filter((s) => s.docId !== sectionDocId),
  //     { docId: sectionDocId, questions: questionsState },
  //   ];
  //   onCustomSectionChange && onCustomSectionChange(updatedAnswers);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [questionsState]);

  useEffect(() => {
    // Helper to check if state is empty (handles both array and object)
    const isEmptyState = (state) => {
      if (!state) return true;
      if (Array.isArray(state)) return state.length === 0;
      return Object.keys(state).length === 0;
    };

    // Skip if this is the initial mount and state is empty (no user input yet)
    if (isEmptyState(questionsState) && !lastSentStateRef.current) {
      return;
    }

    // Mark that we're making an internal update to prevent sync effect from running
    isInternalUpdateRef.current = true;

    const formattedQuestions = Array.isArray(questionsState)
      ? questionsState
      : Object.keys(questionsState).map((id) => ({
        id,
        answer: questionsState[id],
      }));

    // Store what we're sending to prevent circular updates
    lastSentStateRef.current = formattedQuestions;

    const updatedAnswers = [
      ...customSectionAnswers.filter((s) => s.docId !== sectionDocId),
      {
        docId: sectionDocId,
        title: sectionDef?.dataEntrySections?.title || sectionDef?.title || '',
        definition:
          sectionDef?.dataEntrySections?.definition ||
          sectionDef?.definition ||
          '',
        questions: formattedQuestions,
      },
    ];

    onCustomSectionChange && onCustomSectionChange(updatedAnswers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsState, sectionDocId]);


  const getDefaultValue = (questionType) => {
    switch (questionType) {
      case 'Numeric':
        return '';
      case 'MultipleChoice':
        return '';
      case 'MultiSelectDropdown':
        return [];
      case 'TextBox':
        return '';
      case 'TimePicker':
        return null;
      default:
        return '';
    }
  };

  const serializeAnswerValue = (question, value) => {
    if (question.questionType === 'TimePicker') {

      if (!value) return '';
      if (moment.isMoment(value)) {
        return value.isValid() ? value.format('hh:mm A') : '';
      }
      if (value instanceof Date) {
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('hh:mm A') : '';
      }
      if (typeof value === 'string') {
        return value;
      }
      return '';
    }
    return value;
  };

  const parseTimePickerValue = (storedValue) => {

    if (!storedValue) return null;

    const parseFromPrimitive = (value) => {
      if (!value) return null;
      if (value instanceof Date) {
        const parsed = moment(value);
        return parsed.isValid() ? parsed : null;
      }
      if (typeof value === 'string') {
        const parsed = moment(value, ['HH:mm', 'hh:mm A', moment.ISO_8601], true);
        if (!parsed.isValid()) {
          return null;
        }
        return parsed;
      }
      return null;
    };

    if (moment.isMoment(storedValue)) {
      if (typeof storedValue.toDate === 'function') {
        return storedValue;
      }
      return (
        parseFromPrimitive(storedValue._i) ||
        parseFromPrimitive(storedValue._d) ||
        null
      );
    }

    if (storedValue instanceof Date || typeof storedValue === 'string') {
      return parseFromPrimitive(storedValue);
    }

    if (typeof storedValue === 'object') {
      if (storedValue.answer !== undefined) {
        return parseTimePickerValue(storedValue.answer);
      }
      if (storedValue.value !== undefined) {
        return parseTimePickerValue(storedValue.value);
      }
      if (storedValue._i || storedValue._d) {
        return (
          parseFromPrimitive(storedValue._i) ||
          parseFromPrimitive(storedValue._d) ||
          null
        );
      }
    }

    return null;
  };

  const handleCustomDataChange = (sectionId, question, value) => {
    const serializedValue = serializeAnswerValue(question, value);

    // Mark that we're making an internal update before updating state
    isInternalUpdateRef.current = true;

    setQuestionsState((prevQuestions = []) => {
      const currentArray = Array.isArray(prevQuestions)
        ? prevQuestions
        : Object.keys(prevQuestions).map((key) => ({
          id: key,
          answer: prevQuestions[key],
        }));

      // ✅ FIX: explicitly copy only desired fields
      const updated = currentArray.some((q) => q.id === question.id)
        ? currentArray.map((q) =>
          q.id === question.id
            ? {
              id: question.id,
              questionName: question.questionName,
              questionType: question.questionType,
              options: question.options || [],
              answer: serializedValue,
            }
            : q
        )
        : [
          ...currentArray,
          {
            id: question.id,
            questionName: question.questionName,
            questionType: question.questionType,
            options: question.options || [],
            answer: serializedValue,
          },
        ];

      return updated;
    });
  };


  const getQuestionValue = (sectionId, questionId, questionType) => {
    const getStoredValue = () => {
      if (Array.isArray(questionsState)) {
        const questionObj = questionsState.find((q) => q.id === questionId);
        return questionObj ? questionObj.answer : undefined;
      }
      if (questionsState && questionsState[questionId] !== undefined) {
        return questionsState[questionId];
      }
      return undefined;
    };

    if (questionType === 'TimePicker') {
      const storedValue = getStoredValue();
      return parseTimePickerValue(storedValue) ?? getDefaultValue(questionType);
    }

    if (Array.isArray(questionsState)) {
      const questionObj = questionsState.find((q) => q.id === questionId);
      return questionObj ? questionObj.answer ?? getDefaultValue(questionType) : getDefaultValue(questionType);
    }
    if (questionsState && questionsState[questionId] !== undefined) {
      return questionsState[questionId];
    }
    return getDefaultValue(questionType);
  };

  const renderQuestionInput = (question, sectionId) => {
    const value = getQuestionValue(
      sectionId,
      question.id,
      question.questionType
    );

    switch (question.questionType) {
      case 'Numeric':
        return (
          <TextField
            type='number'
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question, e.target.value)
            }
            inputProps={{
              'aria-label': 'Numeric input',
              inputMode: 'numeric',
              onWheel: preventNumberScroll,
            }}
            margin='dense'
            sx={{
              width: '100px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '32px',
                '& .MuiOutlinedInput-input': {
                  padding: '6px 12px',
                  fontSize: '14px',
                },
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6b7280',
                  },
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6b7280',
                  },
                },
              },
            }}
          />
        );

      case 'MultipleChoice':
        return (
          <FormControl fullWidth size='small'>
            <RadioGroup
              value={value}
              onChange={(e) =>
                handleCustomDataChange(sectionId, question, e.target.value)
              }
              sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}
            >
              {question.options?.map((option, optionIndex) => (
                <FormControlLabel
                  key={optionIndex}
                  value={option}
                  control={<Radio size='small' />}
                  label={option}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: 'var(--clr-theme-gray-dark)',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'MultiSelectDropdown':
        return (
          <FormControl fullWidth size='small'>
            <Select
              multiple
              value={value}
              onChange={(e) =>
                handleCustomDataChange(sectionId, question, e.target.value)
              }
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size='small' />
                  ))}
                </Box>
              )}
              className='input-height'
              sx={{
                width: '70%',
                padding: '0',
                backgroundColor: 'var(--clr-mindly-white)',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  '& .MuiOutlinedInput-input': {
                    padding: '6px 12px',
                    fontSize: '14px',
                  },
                },
              }}
            >
              {question.options?.map((option, optionIndex) => (
                <MenuItem key={optionIndex} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'TextBox':
        return (
          <TextField
            multiline
            rows={2}
            fullWidth
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question, e.target.value)
            }
            placeholder='Enter text'
            className='input-height'
            sx={{ width: '70%', padding: '0' }}
          />
        );

      case 'TimePicker':
        // Always use manual time picker for custom data entry
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              value={value}
              onChange={(newValue) =>
                handleCustomDataChange(sectionId, question, newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size='small'
                  className='input-height'
                  sx={{ width: '70%', padding: '0' }}
                />
              )}
            />
          </LocalizationProvider>
        );

      default:
        return (
          <TextField
            fullWidth
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question.id, e.target.value)
            }
            placeholder='Enter value'
            className='input-height'
            sx={{ width: '70%', padding: '0' }}
          />
        );
    }
  };

  // Only show loading if we're actually loading AND this section exists
  if (isLoadingSections && sectionExists) {
    return (
      <Box sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='body2' color='text.secondary'>
            Loading custom section...
          </Typography>
        </Box>
        <LinearProgress
          sx={{
            height: 2,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { backgroundColor: '#9291ef' },
          }}
        />
      </Box>
    );
  }

  // If loading is done but section doesn't exist, don't render anything
  if (!isLoadingSections && !sectionExists) {
    return null;
  }

  if (!sectionDef) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box key={sectionDef.docId} sx={{ mb: 1 }}>
        {sectionDef.dataEntrySections?.questions?.map(
          (question, questionIndex) => (
            <Box key={question.id || questionIndex} sx={{ mb: 1 }}>
              {question.questionType === 'MultipleChoice' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant='body1'>
                    {question.questionName}
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {renderQuestionInput(question, sectionDef.docId)}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography variant='body1' sx={{ minWidth: 'fit-content', pt: '6px' }}>
                    {question.questionName}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {renderQuestionInput(question, sectionDef.docId)}
                  </Box>
                </Box>
              )}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default CustomDataEntrySection;
